import { Layout } from 'react-grid-layout';
import { create } from 'zustand';

import type { Widget } from '@/common/stores/useWidgetStore';

import { supabase } from '@/common/config/supabase';
import { withAuthUser } from '@/common/config/supabaseMiddleware';
import { AVAILABLE_WIDGETS, MAX_FREE_WIDGETS } from '@/common/utils/constants';
import handleException from '@/common/utils/errors';

type Plan = 'free' | 'premium';
type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete';

export interface StripePriceInfo {
  amount: number;
  currency: string;
  interval: string;
}

interface SubscriptionState {
  plan: Plan;
  status: SubscriptionStatus;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  stripeCustomerId: string | null;
  loading: boolean;
  isPremium: boolean;
  prices: Record<string, StripePriceInfo>;
  fetchSubscription: () => Promise<void>;
  fetchPrices: () => Promise<void>;
  createCheckoutSession: (priceId: string) => Promise<string | null>;
  createPortalSession: () => Promise<string | null>;
  canAddWidget: (widgetId: string, currentWidgetCount: number) => boolean;
  isWidgetAvailable: (widgetId: string) => boolean;
  isWidgetLocked: (
    widgetInstanceId: string,
    widgetType: string,
    widgets: Widget[],
    layouts: Layout[],
  ) => boolean;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  plan: 'free',
  status: 'active',
  cancelAtPeriodEnd: false,
  currentPeriodEnd: null,
  stripeCustomerId: null,
  loading: true,
  isPremium: false,
  prices: {},

  fetchSubscription: async () => {
    try {
      await withAuthUser(async (userId) => {
        const { data, error } = await supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (error) throw error;

        if (data) {
          const isPremium =
            data.plan === 'premium' && (data.status === 'active' || data.status === 'trialing');
          set({
            plan: data.plan,
            status: data.status,
            cancelAtPeriodEnd: data.cancel_at_period_end,
            currentPeriodEnd: data.current_period_end,
            stripeCustomerId: data.stripe_customer_id,
            isPremium,
            loading: false,
          });
        } else {
          set({ loading: false });
        }
      });
    } catch (error) {
      handleException(error);
      set({ loading: false });
    }
  },

  fetchPrices: async () => {
    const priceIds = [
      import.meta.env.VITE_STRIPE_PRICE_ID_MONTHLY,
      import.meta.env.VITE_STRIPE_PRICE_ID_ANNUAL,
    ].filter(Boolean);
    if (priceIds.length === 0) return;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/get-prices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token ?? anonKey}`,
        },
        body: JSON.stringify({ priceIds, locale: navigator.language }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch prices');
      }

      const { prices } = await response.json();
      set({ prices: prices ?? {} });
    } catch (error) {
      handleException(error);
    }
  },

  createCheckoutSession: async (priceId: string) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ priceId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      return url;
    } catch (error) {
      handleException(error);
      return null;
    }
  },

  createPortalSession: async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create portal session');
      }

      const { url } = await response.json();
      return url;
    } catch (error) {
      handleException(error);
      return null;
    }
  },

  canAddWidget: (widgetId: string, currentWidgetCount: number) => {
    const { isPremium } = get();
    if (isPremium) return true;

    const widget = AVAILABLE_WIDGETS.find((w) => w.id === widgetId);
    if (!widget) return false;

    if (widget.premium) return false;
    if (currentWidgetCount >= MAX_FREE_WIDGETS) return false;

    return true;
  },

  isWidgetAvailable: (widgetId: string) => {
    const { isPremium } = get();
    if (isPremium) return true;

    const widget = AVAILABLE_WIDGETS.find((w) => w.id === widgetId);
    if (!widget) return false;

    return !widget.premium;
  },

  isWidgetLocked: (
    widgetInstanceId: string,
    widgetType: string,
    widgets: Widget[],
    layouts: Layout[],
  ) => {
    const { isPremium } = get();
    if (isPremium) return false;

    const widgetDef = AVAILABLE_WIDGETS.find((w) => w.id === widgetType);
    if (!widgetDef) return true;

    if (widgetDef.premium) return true;

    const sortedLayouts = [...layouts].sort((a, b) => {
      if (a.y !== b.y) return a.y - b.y;
      return a.x - b.x;
    });

    const unlockedIds = new Set<string>();
    let freeCount = 0;

    for (const layoutItem of sortedLayouts) {
      const widget = widgets.find((w) => w.id === layoutItem.i);
      if (!widget) continue;

      const def = AVAILABLE_WIDGETS.find((w) => w.id === widget.widget_id);
      if (!def || def.premium) continue;

      freeCount++;
      if (freeCount <= MAX_FREE_WIDGETS) {
        unlockedIds.add(widget.id);
      }
    }

    return !unlockedIds.has(widgetInstanceId);
  },
}));
